import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H2 from 'UIComponents/elements/headings/H2';
import H3 from 'UIComponents/elements/headings/H3';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIFlex from 'UIComponents/layout/UIFlex'; // @ts-expect-error untyped dependency

import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import { REVIEWS_PER_PAGE, SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType'; // @ts-expect-error untyped dependency

import MediaSize from 'ecosystem-marketplace-core/components/layout/MediaSize';
import { isUninitialized } from 'ecosystem-marketplace-core/utils/requestState';
import DetailsReviewRenderer from './DetailsReviewRenderer';
import WriteReviewButton from './WriteReviewButton';
import { getReviewFilters, getReviews, getReviewStats, getReviewsTotal // @ts-expect-error untyped dependency
} from '../../../selectors/reviews';
import { getReviewsFetchRequestState, getReviewStatsRequestState // @ts-expect-error untyped dependency
} from '../../../selectors/requestStates';
import ReviewsListEmptyState from './ReviewsListEmptyState';
import { updateReviewFilters, getReviews as fetchReviews, getReviewsStats as fetchReviewStats, resetReviewsFetch, resetReviewsStatsFetch, updateReviewability // @ts-expect-error untyped dependency
} from '../../../actions/ReviewsActions'; // @ts-expect-error untyped dependency

import { makeGetReviewabilityFetchStatus } from '../../../selectors/details';
import ReviewWizard from './ReviewWizard'; // @ts-expect-error untyped dependency

import { replace } from '../../../utils/history';
import { REVIEWS_LIST_WIDTH, MINIMUM_REVIEWS_NEEDED_TO_DISPLAY } from '../../../constants/reviews';
export default function Reviews({
  allReviewsHref,
  appName,
  appIcon,
  isPreview,
  isReviewLink,
  listingDetailUrl,
  offeringId,
  reviewability,
  slug,
  supportLink,
  trackInteraction
}) {
  const reviews = useSelector(getReviews);
  const reviewsFetchStatus = useSelector(getReviewsFetchRequestState);
  const reviewStats = useSelector(getReviewStats);
  const reviewsTotal = useSelector(getReviewsTotal); // Based on current filters

  const reviewsCount = reviewStats.get('count'); // All reviews

  const reviewStatsFetchStatus = useSelector(getReviewStatsRequestState);
  const reviewFilters = useSelector(getReviewFilters);
  reviewFilters.set('entityId', offeringId);
  const getReviewabilityFetchStatus = useMemo(makeGetReviewabilityFetchStatus, []);
  const reviewabilityStatus = useSelector(state => getReviewabilityFetchStatus(state, {
    slug
  }));
  const [wizardOpen, setWizardOpen] = useState(isReviewLink);
  const [searchTerm, setSearchTerm] = useState('');
  const dispatch = useDispatch();
  useEffect(() => {
    if (isUninitialized(reviewsFetchStatus)) {
      dispatch(fetchReviews(reviewFilters.toSearchQuery()));
    }
  }, [dispatch, reviewsFetchStatus, reviewFilters]);
  useEffect(() => {
    if (isUninitialized(reviewStatsFetchStatus)) {
      dispatch(fetchReviewStats(offeringId));
    }
  }, [dispatch, reviewStatsFetchStatus, offeringId]);
  useEffect(() => {
    return () => {
      dispatch(resetReviewsFetch());
      dispatch(resetReviewsStatsFetch());
    };
  }, [dispatch]);
  const renderedWriteReviewButton = useMemo(() => {
    return /*#__PURE__*/_jsx(UIFlex, {
      justify: "end",
      children: /*#__PURE__*/_jsx(WriteReviewButton, {
        className: "m-bottom-4",
        disabled: isPreview,
        offeringId: offeringId,
        onClick: () => setWizardOpen(true),
        reviewability: reviewability
      })
    });
  }, [isPreview, offeringId, reviewability]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    const trackUpdate = () => trackInteraction(`change reviews ${textKey}`, 'review', `${value}`);

    if (textKey === SEARCH_KEY) {
      setSearchTerm(value);
    }

    dispatch(updateReviewFilters({
      [textKey]: value
    }, trackUpdate));
  }, [dispatch, setSearchTerm, trackInteraction]);
  const renderedReviewsList = useMemo(() => /*#__PURE__*/_jsxs("div", {
    style: {
      maxWidth: REVIEWS_LIST_WIDTH
    },
    children: [/*#__PURE__*/_jsx(ReviewsHeader, {
      onUpdateFilters: onUpdateFilters,
      renderedButtonRow: renderedWriteReviewButton,
      reviewsCount: reviewsTotal,
      reviewFilters: reviewFilters
    }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
      onUpdateFilters: onUpdateFilters,
      reviewFilters: reviewFilters,
      reviewStats: reviewStats
    }), /*#__PURE__*/_jsx(ReviewsPaginatedList, {
      onUpdateFilters: onUpdateFilters,
      ReviewRenderer: DetailsReviewRenderer,
      reviews: reviews,
      reviewsFetchStatus: reviewsFetchStatus,
      reviewFilters: reviewFilters,
      reviewsTotal: reviewsTotal,
      showPagination: reviewsTotal >= REVIEWS_PER_PAGE,
      searchTerm: searchTerm
    })]
  }), [onUpdateFilters, reviews, reviewStats, reviewsFetchStatus, reviewsTotal, renderedWriteReviewButton, reviewFilters, searchTerm]);
  const renderedReviewsBody = useMemo(() => {
    if (reviewsCount >= MINIMUM_REVIEWS_NEEDED_TO_DISPLAY) {
      return /*#__PURE__*/_jsxs(UIGrid, {
        className: "m-top-8",
        children: [/*#__PURE__*/_jsxs(UIGridItem, {
          size: {
            xs: 12,
            md: 5,
            lg: 4
          },
          children: [/*#__PURE__*/_jsx(LoadingWrapper, {
            requestStatus: reviewStatsFetchStatus,
            children: /*#__PURE__*/_jsx(ReviewsListStats, {
              allReviewsHref: allReviewsHref,
              onUpdateFilters: onUpdateFilters,
              reviewStats: reviewStats,
              reviewType: ReviewTypes.APP
            })
          }), /*#__PURE__*/_jsx(MediaSize, {
            minScreenSize: "md",
            alternate: /*#__PURE__*/_jsx("div", {
              style: {
                height: 24
              }
            }),
            children: /*#__PURE__*/_jsxs(UIFlex, {
              direction: "column",
              className: "p-top-5",
              children: [/*#__PURE__*/_jsx(H3, {
                "aria-level": 3,
                className: "m-bottom-1 is--heading-5",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "appMarketplace.detail.reviews.write.ctaTitle"
                })
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "appMarketplace.detail.reviews.write.ctaDescription"
              }), /*#__PURE__*/_jsx("div", {
                className: "m-top-4",
                children: /*#__PURE__*/_jsx(WriteReviewButton, {
                  disabled: isPreview,
                  offeringId: offeringId,
                  onClick: () => setWizardOpen(true),
                  reviewability: reviewability,
                  tooltipPlacement: "right bottom"
                })
              })]
            })
          })]
        }), /*#__PURE__*/_jsx(UIGridItem, {
          size: {
            xs: 12,
            md: 7,
            lg: 8
          },
          children: renderedReviewsList
        })]
      });
    } else if (reviewsCount && reviewsCount < MINIMUM_REVIEWS_NEEDED_TO_DISPLAY) {
      return renderedReviewsList;
    } else if (!reviewsCount) {
      return /*#__PURE__*/_jsx(ReviewsListEmptyState, {
        isPreview: isPreview,
        offeringId: offeringId,
        reviewability: reviewability,
        onWriteReviewClick: () => setWizardOpen(true)
      });
    }

    return null;
  }, [allReviewsHref, reviewsCount, renderedReviewsList, reviewStatsFetchStatus, reviewStats, onUpdateFilters, isPreview, offeringId, reviewability]);
  const renderedWizard = useMemo(() => /*#__PURE__*/_jsx(ReviewWizard, {
    appName: appName,
    appIcon: appIcon,
    offeringId: offeringId,
    onClose: () => setWizardOpen(false),
    onSuccess: () => {
      setWizardOpen(false);
      replace(listingDetailUrl);
      dispatch(updateReviewability(slug));
    },
    onTrackInteraction: trackInteraction,
    reviewability: reviewability,
    supportLink: supportLink,
    slug: slug
  }), [appIcon, appName, dispatch, offeringId, listingDetailUrl, trackInteraction, reviewability, slug, supportLink]);
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewabilityStatus,
    children: /*#__PURE__*/_jsxs("div", {
      style: {
        position: 'relative'
      },
      children: [/*#__PURE__*/_jsx(H2, {
        className: "m-bottom-8",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.headingText",
          options: {
            name: appName
          }
        })
      }), renderedReviewsBody, wizardOpen && renderedWizard]
    })
  });
}